/**
 * The game collections published for the market of the brand's site: the market's own catalogue (site `market`) and every
 * casino's there (`codere`, `caliente`…), each with its games in collection order. The list wizard offers a collection as a way to
 * start a hand-picked list: its games, in its order, become the list's games, and the list is then saved like any other
 * hand-picked list.
 *
 * Read through spark-creatives (`GET /creatives/casino-instance/{id}/market-collections?domain=`), which holds the
 * Casino Catalogue API's token and reads the site's country from the site itself. The backend contract is in
 * `spark-creatives/pkg/swagger/swagger.yml`.
 */
import axios from 'axios';

import { creativesRequestConfig, creativesServiceBase, creativesServiceConfigured } from './creative-api';
import { tenantSourceResult, type TenantSourceResult } from './creatives-tenant-source';
import { INSPIRATION_SITES } from './initial-order';
import { sharedReads } from './shared-read';
import { foldText } from './site-games';

/** The site name the catalogue gives the market's own collections, as against a casino's. */
export const MARKET_SITE = 'market';

/** One game on a collection. `name` is empty for an id the platform's catalogue no longer carries: the collection still names it. */
export interface MarketGame {
  id: string;
  name: string;
  providerId: string;
}

/** One published collection: whose it is, where it sits on that site (`Slots › Inicio › Los más populares`) and its games. */
export interface MarketCollection {
  site: string;
  page: string;
  category: string;
  name: string;
  sourceId: string;
  lastUpdated: string;
  games: MarketGame[];
}

/** The service's answer: the market the site is for (`MEX`), the catalogue's name for it (`mx`, '' when it has none)
 *  and the collections published there (none when nothing is). */
export interface MarketCollectionsAnswer {
  siteCountry: string;
  country: string;
  collections: MarketCollection[];
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

const parseGame = (value: unknown): MarketGame | null => {
  if (typeof value !== 'object' || value === null) return null;
  const body = value as Record<string, unknown>;
  const id = typeof body.game_id === 'number' ? String(body.game_id) : text(body.game_id);
  return id === '' ? null : { id, name: text(body.name), providerId: text(body.provider_id) };
};

const parseCollection = (value: unknown): MarketCollection | null => {
  if (typeof value !== 'object' || value === null) return null;
  const body = value as Record<string, unknown>;
  const games = (Array.isArray(body.games) ? body.games : [])
    .map(parseGame)
    .filter((game): game is MarketGame => game !== null);
  return {
    site: text(body.site),
    page: text(body.page),
    category: text(body.category),
    name: text(body.collection),
    sourceId: text(body.source_id),
    lastUpdated: text(body.last_updated),
    games,
  };
};

/** Parse the service's answer. A collection with no games is dropped: it cannot start a list. */
export const parseMarketCollections = (body: Record<string, unknown>): MarketCollectionsAnswer => ({
  siteCountry: text(body.site_country),
  country: text(body.country),
  collections: (Array.isArray(body.collections) ? body.collections : [])
    .map(parseCollection)
    .filter((collection): collection is MarketCollection => collection !== null && collection.games.length > 0),
});

/** The collections published for the market of the brand's site. `domain` is the site's host, as for site-pages. */
export const getMarketCollections = async (
  casinoInstanceId: string,
  domain: string,
  signal?: AbortSignal,
): Promise<TenantSourceResult<MarketCollectionsAnswer>> => {
  if (!creativesServiceConfigured()) return { ok: false, reason: 'unavailable' };
  try {
    const response = await axios.get(
      `${creativesServiceBase()}/creatives/casino-instance/${encodeURIComponent(casinoInstanceId)}/market-collections?domain=${encodeURIComponent(domain)}`,
      creativesRequestConfig(signal),
    );
    return tenantSourceResult(response.status, response.data, parseMarketCollections);
  } catch {
    return { ok: false, reason: 'failed' };
  }
};

/** How long an answer stands for every reader of the brand's market: spark-creatives keeps a market's collections this
 *  long too, and nothing the owner does changes them. */
export const MARKET_COLLECTIONS_CACHE_MS = 300_000;

// The answers that came in, and the reads in flight, per brand and host: every reader shares one.
const marketReads = sharedReads<MarketCollectionsAnswer>(MARKET_COLLECTIONS_CACHE_MS);

/**
 * `getMarketCollections`, shared by every reader of the brand's market (see `sharedReads`). The list wizard's step 2
 * reads the collections each time it comes on show (Back from the name, the method changed); without this each return
 * read the same megabyte again, its loading state up the whole time (a kept answer still lands one render after the
 * step mounts).
 */
export const getMarketCollectionsShared = (
  casinoInstanceId: string,
  domain: string,
): Promise<TenantSourceResult<MarketCollectionsAnswer>> =>
  marketReads.get(`${casinoInstanceId}|${domain}`, () => getMarketCollections(casinoInstanceId, domain));

/** A site's name as the owner reads it: the market's own collections as "Market", a casino the Games tab already names
 *  as its brand writes it the same way (`playuzu` -> `PlayUZU`, see `INSPIRATION_SITES`), any other by its key
 *  (`codere` -> `Codere`, `big_bola-casino` -> `Big Bola Casino`). */
export const siteLabel = (site: string): string => {
  if (site === MARKET_SITE) return 'Market';
  const known = INSPIRATION_SITES.find((name) => name.toLowerCase() === site.toLowerCase());
  return known ?? site.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Where a collection sits on its site, as one line: `Slots › Inicio`, without the parts the catalogue left empty. */
export const collectionPlace = (collection: Pick<MarketCollection, 'page' | 'category'>): string =>
  [collection.page, collection.category].filter((part) => part.trim() !== '').join(' › ');

/**
 * The collections grouped by whose they are, in the order the owner reads them: the market's own first, then the
 * casinos by name. Within a site the collections keep the catalogue's order.
 */
export const collectionsBySite = (
  all: readonly MarketCollection[],
): { site: string; collections: MarketCollection[] }[] => {
  const bySite = new Map<string, MarketCollection[]>();
  for (const collection of all) {
    const list = bySite.get(collection.site);
    if (list) list.push(collection);
    else bySite.set(collection.site, [collection]);
  }
  return [...bySite.entries()]
    .sort(([a], [b]) => (a === MARKET_SITE ? -1 : b === MARKET_SITE ? 1 : a.localeCompare(b)))
    .map(([site, collections]) => ({ site, collections }));
};

/**
 * What a collection brings to a list: its games in collection order, each once, split into those the brand's catalogue carries
 * (`inCatalogue`, the ones the list starts with) and those it does not (`missing`, counted for the owner). `has` says
 * whether the catalogue carries an id.
 */
export const collectionGames = (
  collection: Pick<MarketCollection, 'games'>,
  has: (id: string) => boolean,
): { inCatalogue: string[]; missing: number } => {
  const seen = new Set<string>();
  const inCatalogue: string[] = [];
  let missing = 0;
  for (const { id } of collection.games) {
    if (seen.has(id)) continue;
    seen.add(id);
    if (has(id)) inCatalogue.push(id);
    else missing += 1;
  }
  return { inCatalogue, missing };
};

/** The names casinos give the category a page opens on, folded (`Inicio`, `Página principal del casino`, `HOME`). */
const OPENING_CATEGORY = /^(inicio|home|principal|pagina principal|lobby)\b/;
/** A page of slots, folded (`Slots`, `Tragamonedas`, `Caça-níqueis`). */
const SLOTS_PAGE = /\b(slots?|tragamonedas|tragaperras|caca-niqueis)\b/;

const folded = (value: string): string => foldText(value).trim();

/**
 * A casino's collections in groups by where they sit on its site (`Slots › Inicio`), in the order an owner wants to
 * start from: first the category each page opens on (Slots' before the other pages', then the larger groups first),
 * then the other groups, the larger first. Groups the rules do not tell apart, and the collections within a group, keep
 * the order they came in.
 */
export const collectionsByPlace = (
  collections: readonly MarketCollection[],
): { page: string; category: string; items: MarketCollection[] }[] => {
  const groups = new Map<string, { page: string; category: string; items: MarketCollection[]; first: number }>();
  collections.forEach((collection, index) => {
    const key = `${collection.page}\u0000${collection.category}`;
    const group = groups.get(key);
    if (group) group.items.push(collection);
    else groups.set(key, { page: collection.page, category: collection.category, items: [collection], first: index });
  });
  const rank = (group: { page: string; category: string }): [number, number] => {
    const opening = OPENING_CATEGORY.test(folded(group.category));
    return [opening ? 0 : 1, opening && SLOTS_PAGE.test(folded(group.page)) ? 0 : 1];
  };
  return [...groups.values()]
    .sort((a, b) => {
      const [ra, rb] = [rank(a), rank(b)];
      return ra[0] - rb[0] || ra[1] - rb[1] || b.items.length - a.items.length || a.first - b.first;
    })
    .map(({ page, category, items }) => ({ page, category, items }));
};
